import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {PersonLogin} from "../person/person-login";
import {Router} from "@angular/router";
import {ModelService} from "../services/model-service";
import {PersonLoginService} from "../services/person-login-service";
import { CurrencyPipe } from "@angular/common";
import {FormsModule} from "@angular/forms";
import {SelectionService} from "../services/selection-service";
import {StringUtils} from "../utils/string-utils";
import {SelectionItem} from "../selections/selection-item";
import {Product} from "../products/product";
import {ProductService} from "../services/product-service";
import {firstValueFrom} from "rxjs";
import {GuruEntity} from "../model/guru-entity";
import {Organization} from "../organization/organization";
import {OrganizationService} from "../services/organization-service";
import {ConfigurationData} from "../model/configuration-data";
import {DialogService} from "../services/dialog.service";
import {MatButton} from "@angular/material/button";
import {ConfigurationInputComponent} from "./configuration-input/configuration-input.component";
import {PricingGroup} from "./pricing-group";

@Component({
  selector: 'app-quick-unit-pricing',
  imports: [FormsModule, MatButton, CurrencyPipe, ConfigurationInputComponent],
  templateUrl: './quick-unit-pricing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './quick-unit-pricing.component.css'
})
export class QuickUnitPricingComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly userLoginService = inject(PersonLoginService);
  private readonly selectionService = inject(SelectionService);
  private readonly productService = inject(ProductService);
  private readonly modelService = inject(ModelService);
  private readonly organizationService = inject(OrganizationService);
  private readonly dialogService = inject(DialogService);


  private readonly allSelections = signal<GuruEntity[]>(undefined!);
  private selectedRep!: Organization;
  personLogin!: PersonLogin;
  engineKey!: string;
  sellingOrgId!: string;
  selectedProductId!: string;
  readonly repOffices = signal<SelectionItem[]>(undefined!);
  readonly allProducts = signal<Product[]>(undefined!);
  readonly invalidSelections = signal<string[]>(undefined!);
  readonly messages = signal<string[]>(undefined!);
  readonly configurationData = signal<ConfigurationData>(undefined!);
  pricingGroups!: PricingGroup[];
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.sellingOrgId = '';
    this.personLogin = this.userLoginService.getCachedLogin()!;
    if (!this.userLoginService.isInternal()) {
      this.router.navigate(['/', 'main']).then(() => this.isBusy.set(false));
    }
    this.initializePricingGroups();
    this.selectionService.getControllingOrgs().subscribe({
      next: (results) => {
        this.repOffices.set(results);
        this.productService.getSureFlowConfigurations().subscribe({
          next: (sureFlowProducts: Product[]) => {
            this.isBusy.set(false);
            this.allProducts.set([...sureFlowProducts].sort((first: Product, second: Product) => first.productId.localeCompare(second.productId)));
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  private initializePricingGroups(): void {
    this.pricingGroups = [];
    this.pricingGroups.push(new PricingGroup('Base User Selections', [
      'USUnit', 'USSystemType', 'USUnitVoltage', 'USElecHtKw', 'USElecHtStaging', 'USMotorType', 'USSize',
      'USCoilRows', 'USCoilWallThickness', 'USPriCoilCircuit', 'USSecCoilCircuit']));
    this.pricingGroups.push(new PricingGroup('Indoor Air Quality', ['USFilter', 'USSideFilterAccess', 'USAirPurification']));
    this.pricingGroups.push(new PricingGroup('Mechanical Options', [
      'USHandArrangment', 'USReturnLocation', 'USSupplyUniversal', 'USSupplyFront', 'USSupplyTop', 'USSupplyLeft',
      'USSupplyRight', 'USReversePiping', 'USRiserConn', 'USReturnAirOptions', 'USRAPanelFinish', 'USSupplyType',
      'USSAGrilleFinish', 'USSupplyTypeOBD', 'USSupplyAirOptions', 'USMixingBox', 'USOutsideAir', 'USOutsideAirLocation',
      'USSightBaffle', 'USSameSidePipingCtrls', 'USWallMount', 'USRRDeco', 'USPaintOptions'
    ]));
    this.pricingGroups.push(new PricingGroup('Controls', [
      'USControlVoltage', 'USThermostat', 'USTstatMountLocation', 'USAccessDoorOption', 'USDoorCam', 'USServSw',
      'USControlType', 'USSkyportSetupService', 'USServiceLight', 'USTermStrip', 'USCondensateSwitch'
    ]));
    this.pricingGroups.push(new PricingGroup('Coil Options', [
      'USUnionsOnCoil', 'USCoilEndSheet', 'USCoilTestPressure', 'USAutoVents', 'USDrainValve', 'USPreHeat',
      'USOppositeEnd', 'USRDSystem'
    ]));
    this.pricingGroups.push(new PricingGroup('Drive Assembly', [
      'USMotorType', 'USDriveCodeLetter', 'USNumberTurns', 'USMotorSize', 'USDriveCodeNum', 'USMotorHP'
    ]));
    this.pricingGroups.push(new PricingGroup('Primary Valve Package', [
      'USPrimaryValveSize', 'USPrimaryControlValve', 'USPrimaryIsolationBallValve', 'USPrimaryComboSUPValve',
      'USPrimaryServcFitting', 'USPrimaryStrainer', 'USPrimaryFlowCntl', 'USPrimaryIsolation', 'USPrimaryComboRETValve',
      'USPriBalanceOnBypass', 'USNOvalve1'
    ]));
    this.pricingGroups.push(new PricingGroup('Secondary Valve Package', [
      'USSecondaryValveSize', 'USSecondaryControlValve', 'USSecondaryIsolationBallValve', 'USSecondaryComboSUPValve',
      'USSecondaryServcFitting', 'USSecondaryStrainer', 'USSecondaryFlowCntl', 'USSecondaryIsolation', 'USSecondaryComboRETValve',
      'USSecBalanceOnBypass', 'USNOvalve2'
    ]));
    this.pricingGroups.push(new PricingGroup('Cabinet Features', [
      'USDrainPan', 'USSameSidePipeControls', 'USCabinetInsulation', 'USCabinetHeight', 'USCabinetFootprint', 'USRiserChase',
      'USGSChassis', 'USRetBlkOff', 'USCabExtRight', 'USCabExtHeight', 'USWallBox', 'USInsulatedAOR', 'USTamperProofPanel',
      'USTrimKit', 'USLevelLegs', 'USCabExtLeft', 'USCabExtDepth', 'USValveEnclosure', 'USDripLip', 'USTellTail', 'USLevelingLegs'
    ]));
    this.pricingGroups.push(new PricingGroup('Other Selections', []));
  }

  productChanged(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.updateProductSelections().then(() => this.isBusy.set(false), (error) => this.handleError(error));
  }

  repOfficeChanged(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.updateRepOfficeDetail().then(() => this.isBusy.set(false), (error) => this.handleError(error));
  }

  isRepSelected(): boolean {
    return StringUtils.isNotBlank(this.selectedRep?.orgUnitId);
  }

  private async updateRepOfficeDetail(): Promise<void> {
    if (StringUtils.isNotBlank(this.sellingOrgId)) {
      this.selectedRep = await firstValueFrom<Organization>(this.organizationService.getOrganization(this.sellingOrgId));
      if (this.isUnitSelected()) {
        await this.fetchPrices();
      } else {
        this.selectedProductId = '';
      }
    } else {
      this.selectedProductId = '';
      this.selectedRep = undefined!;
      await this.updateProductSelections();
    }
  }

  private async updateProductSelections(): Promise<void> {
    await this.removeCurrentEngine();
    this.clearProductData();
    if (StringUtils.isNotBlank(this.selectedProductId)) {
      this.engineKey = await firstValueFrom<string>(this.modelService.getEngineKey(this.selectedProductId));
      await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierDefaultOnce'));
      await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierSKU'));
      await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierPRN'));
      await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierDOC'));
      await this.fetchPrices();
    }
  }

  private clearProductData(): void {
    this.allSelections.set([]);
    this.invalidSelections.set([]);
    this.messages.set([]);
    this.configurationData.set(undefined!);
    this.clearUserSelections();
  }

  userSelectionChanged(selection: GuruEntity): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.modelService.setValue(selection.Name, this.engineKey, selection.Value).subscribe({
      next: () => {
        this.recalculatePricing();
      }, error: (error) => this.handleError(error)
    });
  }

  getSelectedPriceListId(): string {
    return this.selectedRep?.priceListId || '';
  }

  recalculatePricing(): void {
    this.isBusy.set(true);
    this.fetchPrices().then(() => this.isBusy.set(false), (error) => this.handleError(error));
  }

  private async fetchPrices(): Promise<void> {
    this.messages.set([]);
    this.configurationData.set(undefined!);
    this.invalidSelections.set(await firstValueFrom<string[]>(this.modelService.getInvalidEntities(this.engineKey)));
    this.allSelections.set(await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierTestInputs')));
    this.categorizeSelections();
    if (this.invalidSelections().length === 0) {
      const priceBasisDate: string = (new Date()).toISOString().substring(0, 10);
      this.configurationData.set(await firstValueFrom<ConfigurationData>(this.modelService.getConfigurationData(this.engineKey, priceBasisDate, this.getSelectedPriceListId())));
    } else {
      this.messages.set(await firstValueFrom<string[]>(this.modelService.getMessages(this.engineKey)));
      this.messages.set(this.messages().filter(message => StringUtils.isNotBlank(message)));
    }
  }

  private categorizeSelections(): void {
    this.clearUserSelections();
    const allUserSelections: GuruEntity[] = JSON.parse(JSON.stringify(this.allSelections()));
    for (const group of this.pricingGroups) {
      for (const selectionName of group.selectionNames) {
        const filtered: GuruEntity[] = allUserSelections.filter((selection: GuruEntity) => selection.Name === selectionName);
        if (filtered.length === 1) {
          group.addSelection(filtered[0]);
          allUserSelections.splice(allUserSelections.indexOf(filtered[0]), 1);
        }
      }
    }
    const otherSelectionsGroup = this.pricingGroups.filter((group: PricingGroup) => group.groupName === 'Other Selections')[0];
    for (const selection of allUserSelections) {
      otherSelectionsGroup.addSelection(selection);
    }
  }

  private clearUserSelections(): void {
    for (const group of this.pricingGroups) {
      group.clearSelections();
    }
  }

  isInvalidSelection(selection: GuruEntity): boolean {
    return (this.invalidSelections()?.length || 0) > 0
      && this.invalidSelections().indexOf(selection.Name) >= 0;
  }

  private async removeCurrentEngine(): Promise<void> {
    if (StringUtils.isNotBlank(this.engineKey)) {
      await firstValueFrom<string>(this.modelService.removeEngine(this.engineKey));
      this.engineKey = undefined!;
    }
  }

  isUnitSelected(): boolean {
    return (this.allSelections()?.length || 0) > 0;
  }

  hasMessages(): boolean {
    return (this.messages()?.length || 0) > 0;
  }

  showMessagesDialog(): void {
    this.dialogService.showConfiguratorMessagesDialog(this.messages());
  }

  getTotalPrice(): number {
    if ((this.configurationData()?.skus?.length || 0) === 0) {
      return 0;
    } else {
      return this.configurationData().skus.map(sku => sku.price).reduce((accumulator, item) => accumulator + item);
    }
  }

  toggleBooleanValue(selection: GuruEntity): void {
    selection.Value = this.isSelected(selection) ? 'false' : 'true';
    this.userSelectionChanged(selection);
  }

  private isSelected(selection: GuruEntity): boolean {
    return StringUtils.equalsIgnoreCase('true', selection.Value);
  }

}
